import { AppAction, Dispatch, ExtraArgs, GetState } from '../../../types'
import { CustomMetricsService } from './util/CustomMetricsService'
import { CustomMetric } from './model'
import { isPCE } from '../../../util/environments'
import { nonProductionRP, productionRP } from './CustomMetricRow/util/GraphService'

export enum ActionType {
  FetchCustomMetrics = 'Monitoring/BuiltIn/CustomMetrics/FetchCustomMetrics',
  FetchedCustomMetrics = 'Monitoring/BuiltIn/CustomMetrics/FetchedCustomMetrics',
  FetchAggregations = 'Monitoring/BuiltIn/CustomMetrics/FetchAggregations',
  FetchedAggregations = 'Monitoring/BuiltIn/CustomMetrics/FetchedAggregations',
  AggregationChanged = 'Monitoring/BuiltIn/CustomMetrics/AggregationChanged',
  FetchMeasures = 'Monitoring/BuiltIn/CustomMetrics/FetchMeasures',
  FetchedMeasures = 'Monitoring/BuiltIn/CustomMetrics/FetchedMeasures',
  MeasureChanged = 'Monitoring/BuiltIn/CustomMetrics/MeasureChanged',
  FetchDimensions = 'Monitoring/BuiltIn/CustomMetrics/FetchDimensions',
  FetchedDimensions = 'Monitoring/BuiltIn/CustomMetrics/FetchedDimensions',
  DimensionChanged = 'Monitoring/BuiltIn/CustomMetrics/DimensionChanged',
}

// Basic Actions

export type FetchCustomMetricsAction = {
  type: ActionType.FetchCustomMetrics
}

export type FetchedCustomMetricsAction = {
  type: ActionType.FetchedCustomMetrics
  payload: { metrics: CustomMetric[] }
}

export type FetchAggregationsAction = {
  type: ActionType.FetchAggregations
  payload: { metric: string }
}

export type FetchedAggregationsAction = {
  type: ActionType.FetchedAggregations
  payload: { aggregations: string[], metric: string, appId: string  }
}

export type AggregationChangedAction = {
  type: ActionType.AggregationChanged
  payload: { selectedAggregation: string, metric: string }
}

export type FetchMeasuresAction = {
  type: ActionType.FetchMeasures
  payload: { metric: string }
}

export type FetchedMeasuresAction = {
  type: ActionType.FetchedMeasures
  payload: { measures: string[], metric: string, appId: string }
}

export type MeasureChangedAction = {
  type: ActionType.MeasureChanged
  payload: { selectedMeasure: string, metric: string }
}

export type FetchDimensionsAction = {
  type: ActionType.FetchDimensions
  payload: { metric: string }
}

export type FetchedDimensionsAction = {
  type: ActionType.FetchedDimensions
  payload: { dimensions: string[], metric: string, appId: string }
}

export type DimensionChangedAction = {
  type: ActionType.DimensionChanged
  payload: { selectedDimension: string, metric: string }
}

export type CustomMetricsAction =
  | FetchCustomMetricsAction
  | FetchedCustomMetricsAction
  | FetchAggregationsAction
  | FetchedAggregationsAction
  | AggregationChangedAction
  | FetchMeasuresAction
  | FetchedMeasuresAction
  | MeasureChangedAction
  | FetchDimensionsAction
  | FetchedDimensionsAction
  | DimensionChangedAction

// Basic Actions Creators

export const fetchCustomMetricsAction = (): AppAction => ({
  type: ActionType.FetchCustomMetrics
})

export const fetchedCustomMetricsAction = (metrics: CustomMetric[]): AppAction => ({
  type: ActionType.FetchedCustomMetrics,
  payload: { metrics }
})

export const fetchAggregationsAction = (metric: string): AppAction => ({
  type: ActionType.FetchAggregations,
  payload: { metric }
})

export const fetchedAggregationsAction = (aggregations: string[], metric: string, appId: string): AppAction => ({
  type: ActionType.FetchedAggregations,
  payload: { aggregations, metric, appId }
})

export const selectedAggregationAction = (selectedAggregation: string, metric: string ): AppAction => ({
  type: ActionType.AggregationChanged,
  payload: { selectedAggregation, metric }
})

export const fetchMeasuresAction = (metric: string): AppAction => ({
  type: ActionType.FetchMeasures,
  payload: { metric }
})

export const fetchedMeasuresAction = (measures: string[], metric: string, appId: string): AppAction => ({
  type: ActionType.FetchedMeasures,
  payload: { measures, metric, appId }
})

export const selectedMeasureAction = (selectedMeasure: string, metric: string ): AppAction => ({
  type: ActionType.MeasureChanged,
  payload: { selectedMeasure, metric }
})

export const fetchDimensionsAction = (metric: string): AppAction => ({
  type: ActionType.FetchDimensions,
  payload: { metric }
})

export const fetchedDimensionsAction = (dimensions: string[], metric: string, appId: string): AppAction => ({
  type: ActionType.FetchedDimensions,
  payload: { dimensions, metric, appId }
})

export const selectedDimensionAction = (selectedDimension: string, metric: string ): AppAction => ({
  type: ActionType.DimensionChanged,
  payload: { selectedDimension, metric }
})

// Thunks

export const fetchAggregations = (metric: string, appId?: string) => (dispatch: Dispatch) => {
  dispatch(fetchAggregationsAction(metric))
  dispatch(fetchedAggregationsAction(CustomMetricsService.getCustomMetricsAggregations(), metric, appId || ''))
}

export const aggregationSelected = (aggregation: string, metric: string, appId?: string) => (dispatch: Dispatch) => {
  if (appId) {
    CustomMetricsService.saveAggregationToLocalStorage(appId, metric, aggregation)
  }

  dispatch(selectedAggregationAction(aggregation, metric))
}

export const fetchMeasures = (metric: string, orgId: string, envId?: string, appId?: string, isProductionEnv?: boolean) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
    dispatch(fetchMeasuresAction(metric))

    const remoteApi = api.ui(getState)
    const customMetricsService = new CustomMetricsService(remoteApi)
  
    const retentionPolicy = isProductionEnv ? productionRP : nonProductionRP
    const resolvedMetric = isPCE() ? `${retentionPolicy}.${metric}` : metric

    customMetricsService
      .fetchCustomMetricFieldKeys(orgId, envId || '', appId || '', resolvedMetric)
      .then(measures => {
        measures.unshift('None')
        dispatch(fetchedMeasuresAction(measures, metric, appId || ''))
      })
      .catch(dispatch(fetchedMeasuresAction([], metric, appId || '')))
}

export const measureSelected = (measure: string, metric: string, appId?: string) => (dispatch: Dispatch) => {
  if (appId) {
    CustomMetricsService.saveMeasureToLocalStorage(appId, metric, measure)
  }

  dispatch(selectedMeasureAction(measure, metric))
}

export const fetchDimensions = (metric: string, orgId: string, envId?: string, appId?: string, from?: string, to?: string, isProdEnv?: boolean) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
    dispatch(fetchDimensionsAction(metric))

    const remoteApi = api.ui(getState)
    const customMetricsService = new CustomMetricsService(remoteApi)
  
    const retentionPolicy = isProdEnv ? productionRP : nonProductionRP
    const resolvedMetric = isPCE() ? `${retentionPolicy}.${metric}` : metric

    customMetricsService
      .fetchCustomMetricDimensions(orgId, envId || '', appId || '', resolvedMetric, from, to)
      .then(dimensions => {
        dimensions.unshift('None')
        dispatch(fetchedDimensionsAction(dimensions, metric, appId || ''))
      })
      .catch(dispatch(fetchedDimensionsAction([], metric, appId || '')))
  }

export const dimensionSelected = (dimension: string, metric: string, appId?: string) => (dispatch: Dispatch) => {
  if (appId) {
    CustomMetricsService.saveDimensionToLocalStorage(appId, metric, dimension)
  }

  dispatch(selectedDimensionAction(dimension, metric))
}

export const fetchCustomMetrics = (orgId: string, envId?: string, appId?: string,
                                   from?: string, to?: string) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  dispatch(fetchCustomMetricsAction())

  const remoteApi = api.ui(getState)
  const customMetricsService = new CustomMetricsService(remoteApi)
  const stateMetrics: any = {...(getState().monitoring.customMetrics.metricList.metrics)}

  if (!envId || !appId) {
    dispatch(fetchedCustomMetricsAction([]))
  } else {
    customMetricsService
      .fetchCustomMetrics(orgId, envId, appId, from, to)
      .then(metrics => {
        dispatch(fetchedCustomMetricsAction(CustomMetricsService.mergeCustomMetricsList(metrics, stateMetrics.metrics, appId)))
      })
      .catch(() => dispatch(fetchedCustomMetricsAction([])))
  }
}
